import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Download, Eye, Loader2, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { artifactClient } from '../../../lib/connect/clients';
import { DocumentPreviewDialog } from '../_shared/components/DocumentPreviewDialog';
/**
 * Renders the real filename of a score's attached source document plus
 * download and in-app preview controls, resolved from the artifact id (the
 * score only stores the id). Reuses tesoro's ArtifactService: getArtifact for
 * the name, generateDownloadUrl for the bytes — both gated same-tenant
 * server-side.
 *
 * The PDF holds the very scores we redact on the wire, so it must not be
 * downloadable OR previewable by a restricted viewer. With enforcement on,
 * attachment_id is itself redacted server-side: a masked viewer's id becomes
 * the "[redacted]" sentinel (so this renders inside the drawer's
 * <MaskedField> as "Restricted"), and a simplified viewer's id is emptied (so
 * the Attachment section is omitted entirely). The <MaskedField> wrapper is
 * belt-and-suspenders — this whole component, preview trigger included, is
 * its `value`, so a masked/simplified viewer never reaches either control
 * even if enforcement is off and the raw id reaches the client.
 */ export function ScoreAttachmentLink({ attachmentId }) {
    const [downloading, setDownloading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const { data, isLoading } = useQuery({
        queryKey: [
            'score-attachment',
            attachmentId
        ],
        queryFn: ()=>artifactClient.getArtifact({
                artifactId: attachmentId
            }),
        staleTime: 5 * 60 * 1000,
        enabled: !!attachmentId
    });
    const filename = data?.artifact?.filename || 'Attached document';
    const onDownload = async ()=>{
        setDownloading(true);
        try {
            const dl = await artifactClient.generateDownloadUrl({
                artifactId: attachmentId
            });
            if (!dl.url) {
                throw new Error('No download URL returned');
            }
            const res = await fetch(dl.url);
            if (!res.ok) {
                throw new Error(`Download failed: ${res.statusText}`);
            }
            const blobUrl = URL.createObjectURL(await res.blob());
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Revoke after the click has kicked off the download — an immediate revoke
            // can race the browser's read of a large blob.
            setTimeout(()=>URL.revokeObjectURL(blobUrl), 0);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Download failed');
        } finally{
            setDownloading(false);
        }
    };
    return /*#__PURE__*/ _jsxs("span", {
        className: "flex items-center gap-2",
        children: [
            /*#__PURE__*/ _jsxs("span", {
                className: "flex items-center gap-1 truncate",
                children: [
                    /*#__PURE__*/ _jsx(Paperclip, {
                        className: "h-3 w-3 shrink-0"
                    }),
                    isLoading ? 'Loading…' : filename
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraButton, {
                type: "button",
                size: "sm",
                variant: "secondary",
                className: "h-6 shrink-0 gap-1 px-2",
                // Wait for the name query so the dialog title is the real filename,
                // not the generic "Attached document" fallback — and stay disabled
                // if the query rejects, since isLoading alone goes false on error
                // too, with no filename ever having resolved.
                disabled: isLoading || !data?.artifact?.filename,
                onClick: ()=>setPreviewOpen(true),
                "data-testid": Testids.deliveryQuality.scoreAttachmentPreview,
                children: [
                    /*#__PURE__*/ _jsx(Eye, {
                        className: "h-3 w-3"
                    }),
                    "Preview"
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraButton, {
                type: "button",
                size: "sm",
                variant: "secondary",
                className: "h-6 shrink-0 gap-1 px-2",
                // Wait for the name query so the saved file keeps its real extension
                // (the a.download fallback "Attached document" has none).
                disabled: downloading || isLoading,
                onClick: onDownload,
                children: [
                    downloading ? /*#__PURE__*/ _jsx(Loader2, {
                        className: "h-3 w-3 animate-spin"
                    }) : /*#__PURE__*/ _jsx(Download, {
                        className: "h-3 w-3"
                    }),
                    "Download"
                ]
            }),
            previewOpen && /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: attachmentId,
                title: filename,
                open: previewOpen,
                onOpenChange: setPreviewOpen
            })
        ]
    });
}
